<script setup lang="ts">
/**
 * 美国地图组件 - 使用ECharts实现真实地图
 * 默认所有州显示为灰色，父组件传入需要高亮的州列表
 */
import type { EChartsOption } from 'echarts'
import { useEcharts } from '~/composables/useEcharts'
import usaMapData from '~/assets/json/us-states.json'
const MAP_ICON = 'https://resources1.interface003.com/web30/2026/07/10/73751532-ae71-419c-9389-e9700b819ff7.png'
const MAP_NAME = 'USA_LICENSE'
const HIGHLIGHTED_HOVER_COLOR = '#AFC5FA'
const MAP_ICON_HEIGHT = 41
const TOOLTIP_ARROW_HEIGHT = 7
const TOOLTIP_ICON_GAP = 5
let mapIconAspectRatio = 1
const mapIconReady = import.meta.client
  ? new Promise<void>((resolve) => {
      const image = new Image()
      image.addEventListener(
        'load',
        () => {
          if (image.naturalWidth > 0) {
            mapIconAspectRatio = image.naturalHeight / image.naturalWidth
          }
          resolve()
        },
        { once: true },
      )
      image.addEventListener('error', () => resolve(), { once: true })
      image.src = MAP_ICON
    })
  : Promise.resolve()

// 将阿拉斯加、夏威夷放入本土地图左下方的空白区域。
const relocatedMapData = JSON.parse(JSON.stringify(usaMapData)) as typeof usaMapData
relocatedMapData.features.push({
  type: 'Feature',
  id: 'PR',
  properties: { name: 'Puerto Rico' },
  geometry: {
    type: 'Polygon',
    coordinates: [
      [
        [-66.448338, 17.984326],
        [-66.771478, 18.006234],
        [-66.924832, 17.929556],
        [-66.985078, 17.973372],
        [-67.209633, 17.956941],
        [-67.154863, 18.19245],
        [-67.269879, 18.362235],
        [-67.094617, 18.515589],
        [-66.957694, 18.488204],
        [-66.409999, 18.488204],
        [-65.840398, 18.433435],
        [-65.632274, 18.367712],
        [-65.626797, 18.203403],
        [-65.730859, 18.186973],
        [-65.834921, 18.017187],
        [-66.234737, 17.929556],
        [-66.448338, 17.984326],
      ],
    ],
  },
} as (typeof usaMapData.features)[number])
const relocatedStateConfig: Record<string, { center: [number, number]; scale: number }> = {
  Alaska: { center: [-119, 25], scale: 0.35 },
  Hawaii: { center: [-105, 25], scale: 1 },
  'Puerto Rico': { center: [-91, 25], scale: 0.7 },
}

const getGeometryPoints = (coordinates: unknown): number[][] => {
  const points: number[][] = []
  const collect = (value: unknown): void => {
    if (!Array.isArray(value)) return
    if (typeof value[0] === 'number') {
      points.push(value as number[])
      return
    }
    value.forEach(collect)
  }
  collect(coordinates)
  return points
}

for (const feature of relocatedMapData.features) {
  const config = relocatedStateConfig[feature.properties.name]
  if (!config) continue

  const points = getGeometryPoints(feature.geometry.coordinates)
  const normalizedPoints = points.map(([longitude, latitude]) => [
    feature.properties.name === 'Alaska' && longitude > 0 ? longitude - 360 : longitude,
    latitude,
  ])
  const sourceCenter: [number, number] = [
    (Math.min(...normalizedPoints.map(([longitude]) => longitude)) +
      Math.max(...normalizedPoints.map(([longitude]) => longitude))) /
      2,
    (Math.min(...normalizedPoints.map(([, latitude]) => latitude)) +
      Math.max(...normalizedPoints.map(([, latitude]) => latitude))) /
      2,
  ]
  let pointIndex = 0
  const relocate = (value: unknown): unknown => {
    if (!Array.isArray(value)) return value
    if (typeof value[0] === 'number') {
      const [longitude, latitude] = normalizedPoints[pointIndex++]
      return [
        config.center[0] + (longitude - sourceCenter[0]) * config.scale,
        config.center[1] + (latitude - sourceCenter[1]) * config.scale,
      ]
    }
    return value.map(relocate)
  }
  feature.geometry.coordinates = relocate(feature.geometry.coordinates) as typeof feature.geometry.coordinates
}
// 州数据类型定义
export interface StateData {
  /** 州代码，如 'WA', 'CA', 'NY' */
  code: string
  /** 州名称（已按当前语言翻译，后端字段为 label），如 '华盛顿州', 'Washington' */
  label?: string
  /** 州英文名称（兼容旧字段），如 'Washington', 'California' */
  name?: string
}

const props = withDefaults(
  defineProps<{
    /** 需要高亮显示的州列表 */
    highlightedStates: StateData[]
    /** 地图容器宽度，避免 popover 内 100% 拿不到尺寸 */
    width?: number
    /** 地图容器高度 */
    height?: number
  }>(),
  { width: 412, height: 306 },
)

// 州代码 -> GeoJSON feature name（英文）。仅用于匹配 ECharts 的 region 与质心，不参与文案展示。
// 文案直接使用后端返回的 name（已按当前语言翻译）。
const codeToGeoName: Record<string, string> = Object.fromEntries(
  (relocatedMapData.features as Array<{ id?: string; properties?: { name?: string } }>)
    .filter((feature) => feature.id && feature.properties?.name)
    .map((feature) => [feature.id as string, feature.properties!.name as string]),
)

// 取州在 GeoJSON 中的英文 feature name（用于匹配 ECharts region / 质心）
const getGeoName = (state: StateData) => codeToGeoName[state.code] || state.name || ''

const getDefaultSelectedStateName = () => {
  const firstState = props.highlightedStates[0]
  return (firstState && getGeoName(firstState)) || null
}
// 计算每个州 GeoJSON 的几何中心
const stateCentroids = computed(() => {
  const result: Record<string, [number, number]> = {}
  for (const feat of (relocatedMapData as any).features) {
    const name = feat.properties?.name as string
    const coords = feat.geometry.coordinates
    const pts: [number, number][] = []
    const collect = (arr: unknown): void => {
      const a = arr as unknown[]
      if (typeof a[0] === 'number') {
        pts.push([a[0] as number, a[1] as number])
      } else {
        for (const sub of a) collect(sub)
      }
    }
    if (feat.geometry.type === 'Polygon') {
      for (const poly of coords) collect(poly)
    } else if (feat.geometry.type === 'MultiPolygon') {
      for (const mp of coords) for (const poly of mp) collect(poly)
    }
    if (pts.length) {
      result[name] = [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length]
    }
  }
  return result
})

// 高亮州数量
const highlightedCount = computed(() => props.highlightedStates.length)

// 容器内联样式，用固定宽高确保 ECharts init 时尺寸正确
const chartStyle = computed(() => ({
  width: `${props.width}px`,
  height: `${props.height}px`,
}))

// 图表容器引用和实例
const mapChartRef = ref<HTMLElement | null>(null)
const selectedStateName = ref<string | null>(getDefaultSelectedStateName())
const selectedTooltipPosition = ref<{
  left: number
  top: number
} | null>(null)
let mapChart: ReturnType<typeof init> | null = null
let resizeHandler: (() => void) | null = null
let resizeObserver: ResizeObserver | null = null

const selectedStateLabel = computed(() => {
  if (!selectedStateName.value) return ''

  const state = props.highlightedStates.find((item) => getGeoName(item) === selectedStateName.value)
  // 后端文案字段为 label（已按当前语言翻译），name 仅作兼容；再兜底英文 GeoJSON 名。
  return state?.label || state?.name || selectedStateName.value
})

const updateSelectedTooltipPosition = () => {
  if (!selectedStateName.value || !mapChart || !mapChartRef.value) {
    selectedTooltipPosition.value = null
    return
  }

  const centroid = stateCentroids.value[selectedStateName.value]
  if (!centroid) {
    selectedTooltipPosition.value = null
    return
  }

  const pixelPosition = mapChart.convertToPixel({ geoIndex: 0 }, centroid)
  if (pixelPosition.length < 2 || !Number.isFinite(pixelPosition[0]) || !Number.isFinite(pixelPosition[1])) {
    selectedTooltipPosition.value = null
    return
  }

  const chartRect = mapChartRef.value.getBoundingClientRect()
  const containerRect = mapChartRef.value.parentElement?.getBoundingClientRect() || chartRect
  const iconHeight = MAP_ICON_HEIGHT
  // ECharts 返回图表内部坐标，浮层挂在地图容器内，需要换算到容器坐标系。
  selectedTooltipPosition.value = {
    left: chartRect.left - containerRect.left + pixelPosition[0],
    top: Math.max(
      8,
      chartRect.top - containerRect.top + pixelPosition[1] - iconHeight - TOOLTIP_ICON_GAP - TOOLTIP_ARROW_HEIGHT,
    ),
  }
}

// 判断当前区域是否为高亮州，仅高亮州区域允许显示浮窗
const isHighlightedState = (name: string) => props.highlightedStates.some((state) => getGeoName(state) === name)

// 获取高亮州对应的 Icon 下标，保持区域和 Icon 共用同一个选中状态
const getIconIndexByName = (name: string) =>
  props.highlightedStates.filter((s) => stateCentroids.value[getGeoName(s)]).findIndex((s) => getGeoName(s) === name)

// 根据当前尺寸生成 Icon 数据，保证 pin 底部尖角始终固定在地理坐标
const buildIconData = () => {
  if (!selectedStateName.value) return []

  const state = props.highlightedStates.find((item) => getGeoName(item) === selectedStateName.value)
  const name = state ? getGeoName(state) : ''
  const centroid = name ? stateCentroids.value[name] : undefined
  if (!name || !centroid) return []

  return [
    {
      name,
      value: centroid,
      symbolSize: [MAP_ICON_HEIGHT / mapIconAspectRatio, MAP_ICON_HEIGHT],
      symbolOffset: [0, -MAP_ICON_HEIGHT / 2],
    },
  ]
}

// 构建ECharts配置项
const buildOption = (): EChartsOption => ({
  animation: false,
  geo: {
    map: MAP_NAME,
    roam: false,
    center: [-96, 36],
    zoom: 1.2,
    silent: false,
    tooltip: {
      show: false,
    },
    label: {
      show: false,
    },
    itemStyle: {
      areaColor: '#F0F1F2',
      borderColor: '#fff',
      borderWidth: 0.8,
    },
    emphasis: {
      disabled: true,
    },
    regions: props.highlightedStates.map((s) => ({
      name: getGeoName(s),
      tooltip: {
        show: true,
      },
      label: {
        show: false,
      },
      itemStyle: {
        areaColor: selectedStateName.value === getGeoName(s) ? HIGHLIGHTED_HOVER_COLOR : '#CFE1FD',
      },
      emphasis: {
        disabled: true,
      },
    })),
  },
  series: [
    // 选中州叠加 icon
    {
      type: 'scatter',
      coordinateSystem: 'geo',
      symbol: 'image://' + MAP_ICON,
      silent: true,
      z: 10,
      data: buildIconData(),
    },
  ],
})

/** hover 高亮州后切换选中州，并显式展示对应 Tooltip。 */
const handleMapMouseover = async (params: any) => {
  if (params.componentType !== 'geo' || !params.name || !isHighlightedState(params.name)) return
  const selectedName = params.name
  if (selectedStateName.value === selectedName) return
  const dataIndex = getIconIndexByName(selectedName)
  if (dataIndex < 0) return

  selectedStateName.value = selectedName
  await mapIconReady
  if (selectedStateName.value !== selectedName || !mapChart) return
  mapChart?.setOption(buildOption(), true)
  requestAnimationFrame(updateSelectedTooltipPosition)
}

// 初始化图表
const { init, load } = useEcharts()

const initChart = async () => {
  if (!import.meta.client || !mapChartRef.value) return

  // 预注册重定位后的美国地图。
  const echarts = await load()
  if (!echarts.getMap(MAP_NAME)) {
    echarts.registerMap(MAP_NAME, relocatedMapData as any)
  }

  mapChart = init(mapChartRef.value, { lazy: true, autoResize: false, renderer: 'canvas' })

  void (async () => {
    const chart = await mapChart?.ready()
    if (!chart) return

    await mapIconReady
    chart.on('mouseover', handleMapMouseover)
    chart.on('finished', updateSelectedTooltipPosition)
    chart.setOption(buildOption(), true)
    chart.resize()
    requestAnimationFrame(updateSelectedTooltipPosition)

    // popover 展开时容器从 0 变为实际尺寸，window resize 不触发，用 ResizeObserver 兜底
    resizeObserver = new ResizeObserver(() => {
      chart.resize()
      chart.setOption(buildOption(), true)
    })
    if (mapChartRef.value) {
      resizeObserver.observe(mapChartRef.value)
    }

    resizeHandler = () => chart.resize()
    window.addEventListener('resize', resizeHandler)
  })().catch(() => undefined)
}
// 监听props变化更新图表
watch(
  () => props.highlightedStates,
  () => {
    if (!selectedStateName.value || !isHighlightedState(selectedStateName.value)) {
      selectedStateName.value = getDefaultSelectedStateName()
    }
    if (mapChart) {
      mapChart.setOption(buildOption(), true)
    }
  },
  { deep: true },
)

onMounted(() => {
  nextTick(() => {
    void initChart()
  })
})

onBeforeUnmount(() => {
  mapChart?.off('mouseover', handleMapMouseover)
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  mapChart?.dispose()
  mapChart = null
})
</script>

<template>
  <div class="map-container">
    <div ref="mapChartRef" class="map-chart" :style="chartStyle"></div>
    <div
      v-if="selectedStateLabel && selectedTooltipPosition"
      class="selected-state-tooltip"
      :style="{
        left: `${selectedTooltipPosition.left}px`,
        top: `${selectedTooltipPosition.top}px`,
      }"
    >
      {{ selectedStateLabel }}
    </div>
    <div class="state-count">{{ highlightedCount }} states</div>
  </div>
</template>

<style scoped lang="less">
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: #f8f8f8;
}

.map-chart {
  /* 尺寸由 props 通过内联 style 控制 */
}

.state-count {
  position: absolute;
  right: 0px;
  bottom: 0px;
  padding: 2px 8px;
  border-radius: 2px;
  background: rgba(5, 12, 31, 0.3);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}

.selected-state-tooltip {
  position: absolute;
  z-index: 20;
  max-width: calc(100% - 24px);
  padding: 8px 12px;
  border-radius: 8px;
  border: 0;
  background: #fff;
  color: #292a2e;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  pointer-events: none;
  text-align: center;
  transform: translate(-50%, -100%);
  white-space: nowrap;

  &::after {
    position: absolute;
    bottom: -7px;
    left: 50%;
    width: 0;
    height: 0;
    border-top: 7px solid #fff;
    border-right: 7px solid transparent;
    border-left: 7px solid transparent;
    content: '';
    transform: translateX(-50%);
  }
}

html[dir='rtl'] .state-count {
  left: 0px;
  right: auto;
}
</style>
